import {useEffect, useState, useRef} from "react";
import {ACTIONS} from "../constants";
import {Alert, App, Button, Input, Space, Table} from "antd";
import {ClipButton, groupData, requestFn} from "../helpers";
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import Fuse from "fuse.js";


export const Events = ({token}) => {
    const [error, setError] = useState('')
    const [data, setData] = useState([])
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 100,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: false,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    })
    
    // Track cursors for each page to enable jumping between pages
    const [pageCursors, setPageCursors] = useState(new Map()) // page -> cursor
    const [hasMoreData, setHasMoreData] = useState(true)
    const currentRequestRef = useRef(null) // Track current request for cancellation

    const {notification, modal} = App.useApp();

    const modalConfig = (updateId, children, id) => ({
        title: children?.length ? `Revert ${children.length} events?` : `Revert event ${id}`,
        content: 'Do you really want to revert these events?',
        onOk: async () => {
            const childrenIds = children ? children.map((item) => item.suid) : [id]
            await requestFn(
                'PATCH',
                '/devices',
                token,
                (res) => {
                    notification.success({
                        message: `Reverted`,
                        description: `Successfully reverted`
                    })
                    setError('')
                    // Just refresh data after revert
                    getData(1, pagination.pageSize, '', true)
                },
                setError
            )
        }
    })

    const getTotalCount = async () => {
        // Only get total count for all events (no filters)
        await requestFn(
            'GET',
            `/events/count`,
            token,
            (response) => {
                setTotalCount(response.total)
                setPagination(prev => ({
                    ...prev,
                    total: response.total,
                    showQuickJumper: response.total > prev.pageSize
                }))
            },
            (error) => {
                console.log('Error fetching events total count:', error)
                setTotalCount(0)
            }
        )
    }

    const getData = async (page = 1, pageSize = pagination.pageSize, searchTerm = search, resetCursors = false) => {
        // Cancel previous request if it exists
        if (currentRequestRef.current) {
            currentRequestRef.current.cancelled = true
        }
        
        // Create new request object
        const currentRequest = { cancelled: false }
        currentRequestRef.current = currentRequest
        
        !loading && setLoading(true)
        
        if (resetCursors) {
            setPageCursors(new Map())
            setHasMoreData(true)
        }
        
        // Get cursor for the requested page
        let cursor = null
        if (page > 1) {
            cursor = pageCursors.get(page - 1) // Get cursor from previous page
        }
        
        const cursorParam = cursor ? `&cursor=${cursor}` : ''
        
        await requestFn(
            'GET', 
            `/events?limit=${pageSize}${cursorParam}`, 
            token, 
            (response) => {
                // Only process response if this request hasn't been cancelled
                if (!currentRequest.cancelled) {
                    setError('')
                    setData(response.data)
                    
                    // Store cursor for next page if there is more data
                    if (response.hasMore && response.nextCursor) {
                        setPageCursors(prev => {
                            const newMap = new Map(prev)
                            newMap.set(page, response.nextCursor)
                            return newMap
                        })
                    }
                    
                    setHasMoreData(response.hasMore || false)
                    
                    setPagination(prev => ({
                        ...prev,
                        current: page,
                        pageSize: pageSize
                    }))
                }
            }, 
            (error) => {
                // Only process error if this request hasn't been cancelled
                if (!currentRequest.cancelled) {
                    setError(error)
                }
            }
        )
        
        // Only set loading to false if this request hasn't been cancelled
        if (!currentRequest.cancelled) {
            setLoading(false)
        }
    }

    const handleTableChange = (paginationInfo) => {
        const { current, pageSize } = paginationInfo
        
        // Don't allow jumping beyond known pages for cursor pagination
        if (current > pagination.current + 1 && !pageCursors.has(current - 1)) {
            return // Prevent jumping too far ahead
        }
        
        getData(current, pageSize, search)
    }

    const getPaginationInfo = () => {
        if (search) {
            // When searching, show current results count
            const filteredData = data?.filter((item) => item.suid?.toLowerCase().includes(search.toLowerCase())) || []
            return `Showing ${filteredData.length} results`
        }
        
        if (totalCount === 0) return ''
        
        const start = (pagination.current - 1) * pagination.pageSize + 1
        const end = Math.min(start + data.length - 1, totalCount)
        
        return `${start}-${end} of ${totalCount.toLocaleString()} items`
    }

    // Separate useEffect for initial data and total count
    useEffect(() => {
        Promise.all([
            getData(),
            getTotalCount()
        ])
    }, []);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'suid',
            key: 'suid',
            render: (suid) => (
                <div style={{display: 'flex', justifyContent: 'space-between' }}>
                    <Link to={`/device/${suid}`}>{suid}</Link>
                    <ClipButton text={suid} />
                </div>
            )
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location'
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp) => new Date(timestamp).toLocaleString()
        },
        {
            title: 'Revert',
            key: 'revert',
            render: (_, record) => (
                <Button 
                    onClick={() => modal.confirm(modalConfig(record.update_id, record.children, record.suid))}
                    type="danger"
                >
                    Revert
                </Button>
            )
        }
    ];

    // Filter tableData based on the search
    const tableData = data?.filter((item) => item.suid?.toLowerCase().includes(search.toLowerCase())) || []

    return (
        <Space direction='vertical' style={{padding: 30, width: '100%'}}>
            {error && <Alert type='error' message={error}/>}
            Search:
            <div style={{display: 'flex', justifyContent: "space-between"}}>
                <div>
                    <Space>
                        <Input
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value)
                                if (error) setError('')
                            }}
                            style={{width: 300}}
                        />
                        <div style={{ 
                            color: '#666', 
                            fontSize: '14px',
                            fontWeight: 500
                        }}>
                            {getPaginationInfo()}
                        </div>
                    </Space>
                </div>
                <Button 
                    loading={loading} 
                    onClick={() => {
                        getData(1, pagination.pageSize, search, true)
                    }}
                >
                    REFETCH
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={tableData}
                pagination={search ? false : pagination} // Hide pagination when searching
                onChange={handleTableChange}
                loading={loading}
                rowKey='suid'
            />
        </Space>
    )
}
